import React, {useState} from 'react';
import {Modal, View, TouchableOpacity, StyleSheet} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';

export const TIME_CONTROL_OPTIONS = ['3+0', '3+2', '5+0', '5+5', '10+0', '10+5'];
export const ROUNDS_OPTIONS = [1, 2, 3];

export const DEFAULT_TIME_CONTROL = '5+0';
export const DEFAULT_ROUNDS = 1;

interface MiniTournamentCreateModalProps {
  visible: boolean;
  isSubmitting?: boolean;
  onSubmit: (payload: {timeControl: string; rounds: number}) => void;
  onClose: () => void;
}

export default function MiniTournamentCreateModal({
  visible,
  isSubmitting,
  onSubmit,
  onClose,
}: MiniTournamentCreateModalProps) {
  const [timeControl, setTimeControl] = useState<string>(DEFAULT_TIME_CONTROL);
  const [rounds, setRounds] = useState<number>(DEFAULT_ROUNDS);

  return (
    <Modal
      transparent
      visible={visible}
      animationType="fade"
      onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <CCText fontSize={FontSizes[18]} fontWeight={600} style={styles.title}>
            Start Mini Tournament
          </CCText>

          <CCText fontSize={FontSizes[14]} style={styles.label}>
            Time control
          </CCText>
          <View style={styles.optionsRow}>
            {TIME_CONTROL_OPTIONS.map(opt => {
              const selected = opt === timeControl;
              return (
                <TouchableOpacity
                  key={opt}
                  onPress={() => setTimeControl(opt)}
                  style={[
                    styles.chip,
                    selected && styles.chipSelected,
                  ]}>
                  <CCText
                    fontSize={FontSizes[13]}
                    color={selected ? CCColors.White : CCColors.TextColor.Primary}>
                    {opt}
                  </CCText>
                </TouchableOpacity>
              );
            })}
          </View>

          <CCText fontSize={FontSizes[14]} style={styles.label}>
            Rounds
          </CCText>
          <View style={styles.optionsRow}>
            {ROUNDS_OPTIONS.map(n => {
              const selected = n === rounds;
              return (
                <TouchableOpacity
                  key={n}
                  onPress={() => setRounds(n)}
                  style={[
                    styles.chip,
                    selected && styles.chipSelected,
                  ]}>
                  <CCText
                    fontSize={FontSizes[13]}
                    color={selected ? CCColors.White : CCColors.TextColor.Primary}>
                    {n}
                  </CCText>
                </TouchableOpacity>
              );
            })}
          </View>

          <View style={styles.actionsRow}>
            <TouchableOpacity
              onPress={onClose}
              disabled={isSubmitting}
              style={[styles.actionBtn, styles.cancelBtn]}>
              <CCText
                fontSize={FontSizes[14]}
                fontWeight={500}
                color={CCColors.TextColor.Primary}>
                Cancel
              </CCText>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => onSubmit({timeControl, rounds})}
              disabled={isSubmitting}
              style={[
                styles.actionBtn,
                styles.submitBtn,
                isSubmitting && styles.submitBtnDisabled,
              ]}>
              <CCText
                fontSize={FontSizes[14]}
                fontWeight={600}
                color={CCColors.White}>
                {isSubmitting ? 'Starting…' : 'Start Tournament'}
              </CCText>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    width: 360,
    backgroundColor: CCColors.White,
    borderRadius: 12,
    padding: 20,
  },
  title: {
    marginBottom: 16,
  },
  label: {
    marginTop: 8,
    marginBottom: 8,
    color: CCColors.TextColor.Primary,
  },
  optionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: CCColors.Grey?.Border ?? '#D0D0D0',
    backgroundColor: CCColors.White,
  },
  chipSelected: {
    backgroundColor: CCColors.Orange,
    borderColor: CCColors.Orange,
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 16,
  },
  actionBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
  },
  cancelBtn: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: CCColors.Grey?.Border ?? '#D0D0D0',
  },
  submitBtn: {
    backgroundColor: CCColors.Orange,
  },
  submitBtnDisabled: {
    backgroundColor: CCColors.OrangeDisabled,
  },
});
